import {
  Box,
  FormControl,
  InputLabel,
  useTheme
} from "@mui/material";
import { useRef, useState } from "react";
import PhoneInput, {
  formatPhoneNumber,
  formatPhoneNumberIntl,
  isValidPhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Select, { components } from "react-select";
import images from "../../constant/images";
import "./style.scss";
import { validationMessages } from "../../helper/validationHelper";
import i18next from "i18next";

export const convertLocalNumber = (num) => {
  let localNumber = formatPhoneNumber(num);
  localNumber = localNumber
    ?.replace(/^0+/, "")
    .replaceAll(" ", "")
    .replaceAll("-", "")
    .replaceAll("(", "")
    .replaceAll(")", "");
  return localNumber;
};

export const getPhoneCountryCode = (num) => {
  const interNational = formatPhoneNumberIntl(num);
  const localNumberArray = interNational?.split(" ");
  return localNumberArray[0];
};

const getFlagUrl = (code) =>
  `https://purecatamphetamine.github.io/country-flag-icons/3x2/${code}.svg`;

const SingleValue = ({ data, ...props }) => (
  <components.SingleValue {...props}>
    <img
      src={getFlagUrl(data.value)}
      alt={data.value}
      style={{ width: "20px", height: "15px", objectFit: "cover", borderRadius: "2px" }}
    />
  </components.SingleValue>
);

const DropdownIndicator = (props) => {
  return (
    <components.IndicatorsContainer {...props}>
      <Box component={"img"} src={images.ArrowDown} />
    </components.IndicatorsContainer>
  );
};

const CustomPhoneInput = ({
  placeholder,
  label = "Phone Number",
  value,
  onChange,
  setErrors,
  name,
}) => {
  const grandParentRef = useRef(null);
  const [isValid, setIsValid] = useState(true);
  const theme = useTheme();

  const CountrySelect = ({ value, onChange, options }) => {
    const selectedOption = options.find((o) => o.value === value);
    const theme = useTheme();

    const formatOptionLabel = ({ value, label }) => {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img
            src={getFlagUrl(value)}
            alt={value}
            style={{
              width: "18px",
              height: "13px",
              objectFit: "cover",
              borderRadius: "2px",
            }}
          />
          <Box
            component="span"
            sx={{
              color: theme.palette.primary.textColor,
            }}
          >
            {label}
          </Box>
        </div>
      );
    };

    return (
      <Select
        value={selectedOption}
        onChange={(option) => onChange(option.value)}
        options={options.slice(1, options.length)}
        formatOptionLabel={formatOptionLabel}
        menuPortalTarget={grandParentRef.current}
        menuPosition="absolute"
        styles={{
          control: (provided) => ({
            ...provided,
            width: "5rem",
            minHeight: "2.5rem",
            height: "2.5rem",
            fontSize: "0.75rem",
            borderRadius: "0.8rem",
            boxShadow: "none",
            backgroundColor: theme.palette.primary.background,
            border: "none",
          }),
          menu: (provided) => ({
            ...provided,
            marginTop: 0,
            marginLeft: -5,
            borderRadius: "0px 0px 0.8rem 0.8rem",
            border: `.1rem solid ${theme.palette.primary.borderColor}`,
            overflow: "hidden",
            zIndex: 2,
            backgroundColor: theme.palette.primary.parentBackground,
          }),
          menuPortal: (provided) => ({
            ...provided,
            paddingTop: 0,
            paddingBottom: 0,
            backgroundColor: theme.palette.primary.parentBackground,
            width: "100%",
            top: 35,
            left: 6,
            zIndex: 2
          }),
          valueContainer: (provided) => ({
            ...provided,
            height: "100%",
            padding: "0",
            justifyContent: "center",
          }),
          singleValue: (provided) => ({
            ...provided,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: 0,
          }),
          option: (provided, state) => ({
            ...provided,
            display: "flex",
            alignItems: "center",
            gap: "5px",
            padding: "0.8rem 1.2rem",
            fontSize: "1.1rem",
            borderTop: `0.1rem solid ${theme.palette.primary.borderColor}`,
            background: theme.palette.primary.parentBackground,
            color: theme.palette.primary.textColor,
            ":active": {
              backgroundColor: theme.palette.primary.parentBackground,
            },
            "&:hover": {
              backgroundColor: theme.palette.primary.parentBackground,
            },
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            height: "100%",
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            padding: "0",
          }),
          separator: () => ({
            display: "none",
          }),
        }}
        isSearchable={false}
        components={{
          IndicatorSeparator: null,
          SingleValue,
          IndicatorsContainer: DropdownIndicator
        }}
      />
    );
  };

  const handleChange = (val) => {
    const valid = val ? isValidPhoneNumber(val) : false;
    setIsValid(valid);
    setErrors((prev) => {
      if (valid === prev[name]) return prev;
      const updated = { ...prev };
      if (!valid) {
        updated[name] = valid;
      } else {
        delete updated[name];
      }
      return updated;
    });
    onChange(val, valid);
  };

  return (
    <FormControl
      ref={grandParentRef}
      fullWidth
      variant="outlined"
      error={!isValid}
      sx={{
        position: "relative",
        "& .PhoneInputInput": {
          backgroundColor: "transparent",
          color: theme.palette.primary.activeText,
          fontSize: "1rem",
          border: "none",
          outline: "none",
          width: "100%",
        },
        "& .PhoneInput": {
          display: "flex",
          alignItems: "center",
          width: "100%",
          border: `0.1rem solid ${!isValid
            ? theme.palette.error.main
            : theme.palette.primary.borderColor
            }`,
          gap: "0.5rem",
          borderRadius: "0.8rem",
          padding: ".55rem 1.3rem 0.55rem 0.4rem",
        },
        "& input:-webkit-autofill": {
          WebkitBoxShadow: "0 0 0 10rem transparent inset !important",
          WebkitTextFillColor: `${theme.palette.primary.activeText} !important`,
          caretColor: theme.palette.primary.activeText,
          transition: "background-color 9999s ease-in-out 0s !important",
        },
        "& input:-webkit-autofill:hover ": {
          WebkitBoxShadow: "0 0 0 10rem transparent inset !important",
          WebkitTextFillColor: `${theme.palette.primary.activeText} !important`,
        },
      }}
    >
      <InputLabel
        shrink
        required
        sx={{
          fontSize: "1.1rem",
          backgroundColor: (theme) => theme.palette.primary.background,
          padding: "0 0.4rem",
          color: (theme) => theme.palette.primary.textColor,
          transform: "translate(12px, -8px) scale(1)!important",
          "& .MuiFormLabel-asterisk": {
            color: (theme) => theme.palette.brandColor.red,
          },
        }}
      >
        {label}
      </InputLabel>

      <PhoneInput
        id={name}
        placeholder={placeholder}
        value={value}
        withCountryCallingCode
        onChange={handleChange}
        international
        defaultCountry="IN"
        className="mui-phone-input"
        countrySelectComponent={CountrySelect}
      />
      <span className="custom-error">
        {!isValid && i18next.t(validationMessages["phone"])}
      </span>
    </FormControl>
  );
};

export default CustomPhoneInput;
